import { AnimatePresence, motion } from "framer-motion";
import {
    ArrowRightOnRectangleIcon,
    Bars3Icon,
    BellAlertIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronUpIcon,
    UserIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import LOGO from "assets/logowhite.png";
import { Avatar, FeatureWrap, Show } from "components/core";
import { Dialog, Disclosure, Menu, Transition } from "@headlessui/react";
import { FC, Fragment } from "react";
import { Link, Navigate, Outlet } from "react-location";
import { applyTheme, generateTheme } from "utils/theme";
import { classNames, wrapClick } from "utils";
import {
    currentTokenVar,
    useCurrentPlaceItDisplayCount,
    useCurrentUser,
} from "_graphql/cache/auth";
import { getAppDetails, withRoutesRoles } from "router/filter";

import KavaNotifications from "./components/kava-notifications";
import KavaNotificationsContainer from "./components/kava-notifications-container";
import LogoutContainer from "./logout";
import NotificationBanner from "components/core/notification-banner";
import Push from "push.js";
import Pusher from "pusher-js";
import React from "react";
import _ from "lodash";
import clsx from "clsx";
import config from "config";
import routes from "router/routes";
import { useIdleTimer } from "react-idle-timer";
import { useMount } from "react-use";
import { useReactiveVar } from "@apollo/client";
import { AIChatPanel } from "./components/AIChatPanel";
import AIButton from "./components/AIButton";

const NAV_SECTIONS = [
    "General",
    "Facultative Businesses",
    "Finance",
    "Treaty",
    "Clients",
    "Place It",
    "Reports",
    "Others",
];

const AppLayout: FC = () => {
    const currentUser = useCurrentUser();
    const currentToken = useReactiveVar(currentTokenVar);
    const [currentSection, setCurrentSection] = React.useState("General");
    const [isCollapsed, setIsCollapsed] = React.useState(false);
    const [showLogout, setShowLogout] = React.useState(false);
    const [showPrompt, setShowPrompt] = React.useState(false);
    const [isKavaNotificationsOpen, setIsKavaNotificationsOpen] =
        React.useState(false);
    const { name, logo } = getAppDetails();
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
    const displayOfferCount = useCurrentPlaceItDisplayCount();

    React.useEffect(() => {
        if (!Push.Permission.has()) {
            Push.Permission.request(onGranted, onDenied);
            Push.Permission.get();
        }
    }, []);

    React.useEffect(() => {
        const pusher = new Pusher(config.pusher.key, {
            cluster: config.pusher.cluster,
        });
        const channel = pusher.subscribe(config.pusher.channel);
        pusher.bind(config.pusher.event, (notif: any) => {
            if (
                JSON.parse(notif.system_data).owner_id !==
                currentUser?.employee?.employee_id
            ) {
                Push.create("Visal-Re Notification", {
                    body: notif.message,
                    icon: logo,
                    timeout: 4000,
                    onClick: function () {},
                });
            }
        });
        return () => {
            pusher.disconnect();
            pusher.unbind_all();
            pusher.unsubscribe(config.pusher.channel);
        };
    }, [currentUser?.employee?.employee_id, logo]);

    const onGranted = () => {
        Push.create(`${name}-Re Notification`, {
            body: `Welcome to ${name} Reinsurance`,
            icon: logo,
            timeout: 4000,
            onClick: () => {
                window.focus();
            },
        });
    };

    const onPresenceChange = (presence: any) => {
        // Handle state changes in one function
        console.log("Hello");
    };

    const onPrompt = () => {
        // Fire a Modal Prompt
        console.log("h1");
        setShowPrompt(true);
    };

    const onIdle = () => {
        // Close Modal Prompt
        // Do some idle action like log out your user
        console.log("Sup");
        setShowPrompt(true);
    };

    const onActive = (event: any) => {
        // Close Modal Prompt
        // Do some active action
        console.log("Ehllo");
        setShowPrompt(false);
    };

    const onAction = (event: any) => {
        // Do something when a user triggers a watched event
        activate();
        reset();
    };

    const { reset, activate } = useIdleTimer({
        onPresenceChange,
        onPrompt,
        onIdle,
        onActive,
        onAction,
        timeout: 1000 * 60 * 15, // 15 minutes
        promptBeforeIdle: 0,
        events: [
            "mousemove",
            "keydown",
            "wheel",
            "DOMMouseScroll",
            "mousewheel",
            "mousedown",
            "touchstart",
            "touchmove",
            "MSPointerDown",
            "MSPointerMove",
            "visibilitychange",
            "focus",
        ],
        immediateEvents: [],
        debounce: 0,
        throttle: 0,
        eventsThrottle: 200,
        element: document,
        startOnMount: true,
        startManually: false,
        stopOnIdle: false,
        crossTab: false,
        name: "idle-timer",
        syncTimers: 0,
        leaderElection: false,
    });

    const onDenied = () => {};

    // --- NEW: memoized navigation list and grouped items by section ---
    const navigation = React.useMemo(
        () =>
            _.chain(routes)
                .filter((route) => _.get(route, "meta.layout") === "App")
                .thru(withRoutesRoles)
                .filter((item) => !!item.sidebar)
                .value(),
        [],
    );

    const sectionItems = React.useMemo(
        () => _.groupBy(navigation, (item) => _.get(item, "meta.section")),
        [navigation],
    );

    const visibleSections = React.useMemo(
        () => NAV_SECTIONS.filter((s) => (sectionItems[s]?.length ?? 0) > 0),
        [sectionItems],
    );

    // Safety: if the current section has become empty, switch to the first visible one
    React.useEffect(() => {
        if (!(sectionItems[currentSection]?.length > 0)) {
            const next = visibleSections[0];
            if (next && next !== currentSection) setCurrentSection(next);
        }
    }, [currentSection, sectionItems, visibleSections]);

    React.useEffect(() => {
        const theme = generateTheme(
            currentUser?.employee?.broker_config?.broker_color_schema ||
                "#4F46E5",
        );
        applyTheme(theme);
    }, []);

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    // Mobile navigation content
    const MobileNav = () => (
        <Dialog
            as="div"
            className={clsx("fixed  inset-0 z-40 lg:hidden", {
                dark: config.appDomain === "Visal",
                // "dark": config.appDomain === "Visal",
            })}
            open={mobileMenuOpen}
            onClose={setMobileMenuOpen}>
            <div
                className="fixed inset-0 bg-black bg-opacity-40"
                aria-hidden="true"
            />
            <div className="fixed inset-y-0 left-0 max-w-full flex">
                <Dialog.Panel className="w-72 bg-sidebar-500 border-r border-sidebar-500 flex flex-col h-full shadow-xl">
                    <div className="flex items-center justify-between p-4 border-b border-sidebar-700">
                        <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 p-1 bg-gray-200">
                                <img
                                    className="h-full w-full aspect-h-1 aspect-w-1 border-white border p-1"
                                    src={logo}
                                    alt="CRBS"
                                />
                            </div>
                            <div className="ml-3 flex-1">
                                <p className="text-xs text-white ">{name}</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setMobileMenuOpen(false)}
                            className="text-white hover:text-gray-300">
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>
                    <div className="py-4 flex-1 flex flex-col overflow-y-auto no-scrollbar">
                        <nav
                            className="flex-1 space-y-6 px-2"
                            aria-label="Sidebar">
                            {visibleSections.map((section) => (
                                <Disclosure
                                    defaultOpen={currentSection === section}
                                    key={section}>
                                    {() => (
                                        <div className="space-y-1">
                                            <Disclosure.Button
                                                onClick={() => {
                                                    setCurrentSection(section);
                                                }}
                                                className="flex w-full justify-between rounded-lg pr-2 py-2 text-left text-sm font-medium focus:outline-none focus-visible:ring focus-visible:ring-purple-500/75">
                                                <h3
                                                    className="px-3 text-sm  text-gray-300"
                                                    id={section}>
                                                    {section}
                                                </h3>
                                                <div className="flex items-center space-x-2 justify-center">
                                                    <Show
                                                        if={
                                                            section ===
                                                                "Place It" &&
                                                            Number(
                                                                displayOfferCount,
                                                            ) > 0 &&
                                                            currentSection !==
                                                                section
                                                        }>
                                                        <motion.span
                                                            className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full transform translate-x-1/4 -translate-y-1/4"
                                                            aria-label={`${displayOfferCount} Kava notifications`}
                                                            initial={{
                                                                scale: 0,
                                                            }}
                                                            animate={{
                                                                scale: 1,
                                                            }}
                                                            transition={{
                                                                type: "spring",
                                                                stiffness: 500,
                                                            }}>
                                                            {Number(
                                                                displayOfferCount,
                                                            ) > 9
                                                                ? "9+"
                                                                : displayOfferCount}
                                                        </motion.span>
                                                    </Show>
                                                    <ChevronUpIcon
                                                        className={`${
                                                            currentSection !==
                                                            section
                                                                ? "rotate-180 transform"
                                                                : ""
                                                        } h-4 w-4 text-gray-300`}
                                                    />
                                                </div>
                                            </Disclosure.Button>
                                            <AnimatePresence>
                                                {currentSection === section && (
                                                    <motion.div
                                                        initial={{
                                                            height: 0,
                                                            opacity: 0,
                                                        }}
                                                        animate={{
                                                            height: "auto",
                                                            opacity: 1,
                                                            transition: {
                                                                height: {
                                                                    duration: 0.3,
                                                                },
                                                                opacity: {
                                                                    duration: 0.2,
                                                                    delay: 0.1,
                                                                },
                                                            },
                                                        }}
                                                        exit={{
                                                            height: 0,
                                                            opacity: 0,
                                                            transition: {
                                                                height: {
                                                                    duration: 0.2,
                                                                },
                                                                opacity: {
                                                                    duration: 0.1,
                                                                },
                                                            },
                                                        }}
                                                        className="overflow-hidden">
                                                        <div
                                                            className="space-y-1"
                                                            role="group"
                                                            aria-labelledby={
                                                                section
                                                            }>
                                                            {(
                                                                sectionItems[
                                                                    section
                                                                ] || []
                                                            ).map((item) => {
                                                                return (
                                                                    <Link
                                                                        key={
                                                                            item
                                                                                .sidebar
                                                                                ?.label
                                                                        }
                                                                        to={
                                                                            item.path
                                                                        }
                                                                        getActiveProps={() => ({
                                                                            className:
                                                                                "bg-secondary-800 text-white",
                                                                        })}
                                                                        getInactiveProps={() => ({
                                                                            className:
                                                                                "text-gray-300 hover:bg-secondary-800 hover:text-white",
                                                                        })}
                                                                        className="group flex items-center px-2 py-2 text-sm justify-between rounded-md"
                                                                        onClick={() =>
                                                                            setMobileMenuOpen(
                                                                                false,
                                                                            )
                                                                        }>
                                                                        <div className="flex items-center">
                                                                            {item
                                                                                .sidebar
                                                                                ?.icon && (
                                                                                <item.sidebar.icon
                                                                                    className={clsx(
                                                                                        "text-gray-200 group-hover:text-gray-50",
                                                                                        "flex-shrink-0 h-6 w-6 mr-3",
                                                                                    )}
                                                                                    aria-hidden="true"
                                                                                />
                                                                            )}
                                                                            {
                                                                                item
                                                                                    .sidebar
                                                                                    ?.label
                                                                            }
                                                                        </div>
                                                                        <Show
                                                                            if={
                                                                                item
                                                                                    .sidebar
                                                                                    ?.label ===
                                                                                    "Broker Offers" &&
                                                                                Number(
                                                                                    displayOfferCount,
                                                                                ) >
                                                                                    0 &&
                                                                                currentSection ===
                                                                                    section
                                                                            }>
                                                                            <motion.span
                                                                                className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full"
                                                                                aria-label={`${displayOfferCount} Kava notifications`}
                                                                                initial={{
                                                                                    scale: 0,
                                                                                }}
                                                                                animate={{
                                                                                    scale: 1,
                                                                                }}
                                                                                transition={{
                                                                                    type: "spring",
                                                                                    stiffness: 500,
                                                                                }}>
                                                                                {Number(
                                                                                    displayOfferCount,
                                                                                ) >
                                                                                9
                                                                                    ? "9+"
                                                                                    : displayOfferCount}
                                                                            </motion.span>
                                                                        </Show>
                                                                    </Link>
                                                                );
                                                            })}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    )}
                                </Disclosure>
                            ))}
                        </nav>
                        <nav className="space-y-1 px-2">
                            <KavaNotifications
                                isCollapsed={false}
                                setOpen={setIsKavaNotificationsOpen}
                            />
                            <Link
                                to={"/notifications"}
                                className={clsx(
                                    "group flex items-center px-2 py-2 text-sm rounded-md text-gray-300 hover:bg-card-800 hover:text-white relative",
                                )}
                                title="Notifications"
                                onClick={() => setMobileMenuOpen(false)}>
                                <BellAlertIcon
                                    className={clsx(
                                        "text-gray-200 group-hover:text-gray-50 flex-shrink-0 h-6 w-6",
                                        "mr-3",
                                    )}
                                    aria-hidden="true"
                                />
                                Notifications
                            </Link>
                        </nav>
                    </div>
                    <Menu as="div" className="relative">
                        <Menu.Button className="flex flex-shrink-0 border-t border-card-600 p-4 overflow-hidden w-full">
                            <div className="flex-1 flex items-center space-x-3 overflow-hidden">
                                <div className="flex-shrink-0">
                                    <Avatar
                                        size="md"
                                        alt={
                                            currentUser?.employee?.emp_abbrv ||
                                            "N A"
                                        }
                                    />
                                </div>
                                <motion.div
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -10 }}
                                    className="flex-1 overflow-hidden">
                                    <p className="text-sm font-medium text-gray-200 group-hover:text-white line-clamp-1">
                                        {[
                                            currentUser?.employee
                                                ?.employee_first_name || "",
                                            currentUser?.employee
                                                ?.employee_last_name || "",
                                        ]
                                            .join(" ")
                                            .trim()}
                                    </p>
                                    <p className="text-xs font-medium text-gray-300 group-hover:text-gray-100 line-clamp-1 text-ellipsis overflow-hidden">
                                        {currentUser?.employee?.employee_email}
                                    </p>
                                </motion.div>
                            </div>
                        </Menu.Button>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95">
                            <Menu.Items className="absolute right-0 bottom-full z-10 mb-2 w-48 origin-bottom-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                <div className="py-1">
                                    <Menu.Item>
                                        {({ active }) => (
                                            <Link
                                                className={classNames(
                                                    active ? "bg-gray-50" : "",
                                                    "flex w-full items-center px-4 py-2 text-sm text-gray-700",
                                                )}
                                                to="/profile"
                                                onClick={() =>
                                                    setMobileMenuOpen(false)
                                                }>
                                                <UserIcon className="mr-3 h-5 w-5 text-gray-400" />
                                                Profile
                                            </Link>
                                        )}
                                    </Menu.Item>

                                    <Menu.Item>
                                        {({ active }) => (
                                            <button
                                                className={classNames(
                                                    active ? "bg-gray-50" : "",
                                                    "flex w-full items-center px-4 py-2 text-sm text-red-600",
                                                )}
                                                onClick={wrapClick(() => {
                                                    setShowLogout(true);
                                                    setMobileMenuOpen(false);
                                                })}>
                                                <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-red-400" />
                                                Logout
                                            </button>
                                        )}
                                    </Menu.Item>
                                </div>
                            </Menu.Items>
                        </Transition>
                    </Menu>
                </Dialog.Panel>
            </div>
        </Dialog>
    );

    if (!currentToken) {
        return <Navigate to={"/cpanel-login"} replace />;
    }

    return (
        <>
            {/* Mobile top bar */}
            <div className="lg:hidden flex items-center justify-between px-4 py-2 bg-sidebar-500 border-b border-sidebar-700">
                <div className="flex items-center">
                    <button
                        onClick={() => setMobileMenuOpen(true)}
                        className="text-white hover:text-gray-300 focus:outline-none"
                        aria-label="Open menu">
                        <Bars3Icon className="h-7 w-7" />
                    </button>
                    <div className="flex-shrink-0 flex flex-col items-start ">
                        <img className="h-6 w-auto" src={LOGO} alt="SUBS" />

                        <div className="ml-3 mt-1 flex-1">
                            <p className="text-xs text-white ">{name}</p>
                        </div>
                    </div>
                </div>
                <Menu as="div" className="relative">
                    <Menu.Button className="flex items-center focus:outline-none">
                        <Avatar
                            size="sm"
                            alt={currentUser?.employee?.emp_abbrv || "N A"}
                        />
                    </Menu.Button>
                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95">
                        <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                            <div className="py-1">
                                <Menu.Item>
                                    {({ active }) => (
                                        <Link
                                            className={classNames(
                                                active ? "bg-gray-50" : "",
                                                "flex w-full items-center px-4 py-2 text-sm text-gray-700",
                                            )}
                                            to="/profile">
                                            <UserIcon className="mr-3 h-5 w-5 text-gray-400" />
                                            Profile
                                        </Link>
                                    )}
                                </Menu.Item>
                                <Menu.Item>
                                    {({ active }) => (
                                        <button
                                            className={classNames(
                                                active ? "bg-gray-50" : "",
                                                "flex w-full items-center px-4 py-2 text-sm text-red-600",
                                            )}
                                            onClick={wrapClick(() =>
                                                setShowLogout(true),
                                            )}>
                                            <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-red-400" />
                                            Logout
                                        </button>
                                    )}
                                </Menu.Item>
                            </div>
                        </Menu.Items>
                    </Transition>
                </Menu>
            </div>
            {/* Mobile navigation drawer */}
            {mobileMenuOpen && <MobileNav />}

            <div className="h-screen">
                <div className="h-full flex">
                    {/* Desktop sidebar */}
                    <motion.div
                        className="hidden lg:flex lg:flex-shrink-0 "
                        initial={false}
                        animate={{
                            width: isCollapsed ? "75px" : "16rem",
                        }}
                        style={{
                            backgroundColor: "rgb(var(--color-background-100))",
                        }}
                        transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 30,
                        }}>
                        <motion.div
                            className="flex flex-col border-r border-sidebar-500 bg-sidebar-500 h-full w-full"
                            layout>
                            <div className="flex flex-shrink-0 items-center border-b border-sidebar-700 p-4 flex-row justify-between">
                                {!isCollapsed && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        className=" flex-shrink-0 flex flex-col items-start">
                                        <img
                                            className="h-10 w-auto"
                                            src={LOGO}
                                            alt="SUBS"
                                        />

                                        <div className="ml-3 mt-1 flex-1">
                                            <p className="text-xs text-white ">
                                                {name}
                                            </p>
                                        </div>
                                    </motion.div>
                                )}
                                <button
                                    onClick={toggleSidebar}
                                    className="text-white hover:text-gray-300">
                                    {isCollapsed ? (
                                        <ChevronRightIcon className="h-5 w-5" />
                                    ) : (
                                        <ChevronLeftIcon className="h-5 w-5" />
                                    )}
                                </button>
                            </div>

                            <div className="py-4 flex min-h-0 flex-1 flex-col overflow-y-auto no-scrollbar">
                                <nav
                                    className="flex-1 space-y-6 px-2"
                                    aria-label="Sidebar">
                                    {isCollapsed ? (
                                        <div className="space-y-1">
                                            {navigation.map((item) => (
                                                <Link
                                                    key={item.sidebar?.label}
                                                    to={item.path}
                                                    getActiveProps={() => ({
                                                        className:
                                                            "bg-link-800 text-white",
                                                    })}
                                                    getInactiveProps={() => ({
                                                        className:
                                                            "text-gray-300 hover:bg-secondary-800 hover:text-white",
                                                    })}
                                                    className="group flex items-center justify-center px-2 py-2 text-sm rounded-md relative"
                                                    title={item.sidebar?.label}>
                                                    {item.sidebar?.icon && (
                                                        <item.sidebar.icon
                                                            className="text-gray-200 group-hover:text-gray-50 flex-shrink-0 h-6 w-6"
                                                            aria-hidden="true"
                                                        />
                                                    )}
                                                    <motion.span
                                                        className="absolute left-full ml-2 w-auto min-w-max rounded bg-black text-xs text-white p-2 opacity-0"
                                                        whileHover={{
                                                            opacity: 1,
                                                        }}
                                                        transition={{
                                                            duration: 0.2,
                                                        }}>
                                                        {item.sidebar?.label}
                                                    </motion.span>
                                                </Link>
                                            ))}
                                        </div>
                                    ) : (
                                        visibleSections.map((section) => (
                                            <Disclosure
                                                defaultOpen={
                                                    currentSection === section
                                                }
                                                key={section}>
                                                {() => (
                                                    <div className="space-y-1">
                                                        <Disclosure.Button
                                                            onClick={() => {
                                                                setCurrentSection(
                                                                    section,
                                                                );
                                                            }}
                                                            className="flex w-full justify-between rounded-lg pr-2 py-2 text-left text-sm font-medium focus:outline-none focus-visible:ring focus-visible:ring-purple-500/75">
                                                            <h3
                                                                className="px-3 text-xs  text-gray-300"
                                                                id={section}>
                                                                {section}
                                                            </h3>

                                                            <div className="flex items-center space-x-2 justify-center">
                                                                <Show
                                                                    if={
                                                                        section ===
                                                                            "Place It" &&
                                                                        Number(
                                                                            displayOfferCount,
                                                                        ) > 0 &&
                                                                        currentSection !==
                                                                            section
                                                                    }>
                                                                    <motion.span
                                                                        className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full transform translate-x-1/4 -translate-y-1/4"
                                                                        aria-label={`${displayOfferCount} Kava notifications`}
                                                                        initial={{
                                                                            scale: 0,
                                                                        }}
                                                                        animate={{
                                                                            scale: 1,
                                                                        }}
                                                                        transition={{
                                                                            type: "spring",
                                                                            stiffness: 500,
                                                                        }}>
                                                                        {Number(
                                                                            displayOfferCount,
                                                                        ) > 9
                                                                            ? "9+"
                                                                            : displayOfferCount}
                                                                    </motion.span>
                                                                </Show>
                                                                <ChevronUpIcon
                                                                    className={`${
                                                                        currentSection !==
                                                                        section
                                                                            ? "rotate-180 transform"
                                                                            : ""
                                                                    } h-4 w-4 text-gray-300`}
                                                                />
                                                            </div>
                                                        </Disclosure.Button>
                                                        <AnimatePresence>
                                                            {currentSection ===
                                                                section && (
                                                                <motion.div
                                                                    initial={{
                                                                        height: 0,
                                                                        opacity: 0,
                                                                    }}
                                                                    animate={{
                                                                        height: "auto",
                                                                        opacity: 1,
                                                                        transition:
                                                                            {
                                                                                height: {
                                                                                    duration: 0.3,
                                                                                },
                                                                                opacity:
                                                                                    {
                                                                                        duration: 0.2,
                                                                                        delay: 0.1,
                                                                                    },
                                                                            },
                                                                    }}
                                                                    exit={{
                                                                        height: 0,
                                                                        opacity: 0,
                                                                        transition:
                                                                            {
                                                                                height: {
                                                                                    duration: 0.2,
                                                                                },
                                                                                opacity:
                                                                                    {
                                                                                        duration: 0.1,
                                                                                    },
                                                                            },
                                                                    }}
                                                                    className="overflow-hidden">
                                                                    <div
                                                                        className="space-y-1"
                                                                        role="group"
                                                                        aria-labelledby={
                                                                            section
                                                                        }>
                                                                        {(
                                                                            sectionItems[
                                                                                section
                                                                            ] ||
                                                                            []
                                                                        ).map(
                                                                            (
                                                                                item,
                                                                            ) => {
                                                                                const isCurrent = false;
                                                                                return (
                                                                                    <Link
                                                                                        key={
                                                                                            item
                                                                                                .sidebar
                                                                                                ?.label
                                                                                        }
                                                                                        to={
                                                                                            item.path
                                                                                        }
                                                                                        getActiveProps={() => ({
                                                                                            className:
                                                                                                "bg-link-800 text-white",
                                                                                        })}
                                                                                        getInactiveProps={() => ({
                                                                                            className:
                                                                                                "text-gray-300 hover:bg-secondary-800 hover:text-white",
                                                                                        })}
                                                                                        className="group flex items-center px-2 py-2 text-xs justify-between rounded-md">
                                                                                        <div className="flex items-center">
                                                                                            {item
                                                                                                .sidebar
                                                                                                ?.icon && (
                                                                                                <item.sidebar.icon
                                                                                                    className={clsx(
                                                                                                        isCurrent
                                                                                                            ? "text-gray-900"
                                                                                                            : "text-gray-200 group-hover:text-gray-50",
                                                                                                        "flex-shrink-0 h-5 w-5 mr-3",
                                                                                                    )}
                                                                                                    aria-hidden="true"
                                                                                                />
                                                                                            )}
                                                                                            {
                                                                                                item
                                                                                                    .sidebar
                                                                                                    ?.label
                                                                                            }
                                                                                        </div>
                                                                                        <Show
                                                                                            if={
                                                                                                item
                                                                                                    .sidebar
                                                                                                    ?.label ===
                                                                                                    "Broker Offers" &&
                                                                                                Number(
                                                                                                    displayOfferCount,
                                                                                                ) >
                                                                                                    0 &&
                                                                                                currentSection ===
                                                                                                    section
                                                                                            }>
                                                                                            <motion.span
                                                                                                className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full"
                                                                                                aria-label={`${displayOfferCount} Kava notifications`}
                                                                                                initial={{
                                                                                                    scale: 0,
                                                                                                }}
                                                                                                animate={{
                                                                                                    scale: 1,
                                                                                                }}
                                                                                                transition={{
                                                                                                    type: "spring",
                                                                                                    stiffness: 500,
                                                                                                }}>
                                                                                                {Number(
                                                                                                    displayOfferCount,
                                                                                                ) >
                                                                                                9
                                                                                                    ? "9+"
                                                                                                    : displayOfferCount}
                                                                                            </motion.span>
                                                                                        </Show>
                                                                                    </Link>
                                                                                );
                                                                            },
                                                                        )}
                                                                    </div>
                                                                </motion.div>
                                                            )}
                                                        </AnimatePresence>
                                                    </div>
                                                )}
                                            </Disclosure>
                                        ))
                                    )}
                                </nav>
                                <nav className="space-y-1 px-2">
                                    <KavaNotifications
                                        isCollapsed={isCollapsed}
                                        setOpen={setIsKavaNotificationsOpen}
                                    />

                                    <Link
                                        to={"/notifications"}
                                        className={clsx(
                                            "group flex items-center px-2 py-2 text-sm rounded-md text-gray-300 hover:bg-sidebar-800 hover:text-white relative",
                                            isCollapsed && "justify-center",
                                        )}
                                        title="Notifications">
                                        <BellAlertIcon
                                            className={clsx(
                                                "text-gray-200 group-hover:text-gray-50 flex-shrink-0 h-6 w-6",
                                                !isCollapsed && "mr-3",
                                            )}
                                            aria-hidden="true"
                                        />
                                        {!isCollapsed && "Notifications"}
                                        {isCollapsed && (
                                            <motion.span
                                                className="absolute left-full ml-2 w-auto min-w-max rounded bg-black text-xs text-white p-2 opacity-0"
                                                whileHover={{ opacity: 1 }}
                                                transition={{ duration: 0.2 }}>
                                                Notifications
                                            </motion.span>
                                        )}
                                    </Link>
                                </nav>
                            </div>
                            <Menu as="div" className="relative">
                                <Menu.Button className="flex flex-shrink-0 border-t border-card-600 p-4 overflow-hidden">
                                    <div className="flex-1 flex items-center space-x-3 overflow-hidden">
                                        <div className="flex-shrink-0">
                                            <Avatar
                                                size={isCollapsed ? "sm" : "md"}
                                                alt={
                                                    currentUser?.employee
                                                        ?.emp_abbrv || "N A"
                                                }
                                            />
                                        </div>
                                        {!isCollapsed && (
                                            <motion.div
                                                initial={{ opacity: 0, x: -10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{ opacity: 0, x: -10 }}
                                                className="flex-1 overflow-hidden">
                                                <p className="text-sm font-medium text-gray-200 group-hover:text-white line-clamp-1">
                                                    {[
                                                        currentUser?.employee
                                                            ?.employee_first_name ||
                                                            "",
                                                        currentUser?.employee
                                                            ?.employee_last_name ||
                                                            "",
                                                    ]
                                                        .join(" ")
                                                        .trim()}
                                                </p>
                                                <p className="text-xs font-medium text-gray-300 group-hover:text-gray-100 line-clamp-1 text-ellipsis overflow-hidden">
                                                    {
                                                        currentUser?.employee
                                                            ?.employee_email
                                                    }
                                                </p>
                                            </motion.div>
                                        )}
                                    </div>
                                </Menu.Button>
                                <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform opacity-0 scale-95"
                                    enterTo="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform opacity-100 scale-100"
                                    leaveTo="transform opacity-0 scale-95">
                                    <Menu.Items className="absolute right-0 bottom-full z-10 mb-2 w-48 origin-bottom-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                        <div className="py-1">
                                            <Menu.Item>
                                                {({ active }) => (
                                                    <Link
                                                        className={classNames(
                                                            active
                                                                ? "bg-gray-50"
                                                                : "",
                                                            "flex w-full items-center px-4 py-2 text-sm text-gray-700",
                                                        )}
                                                        to="/profile">
                                                        <UserIcon className="mr-3 h-5 w-5 text-gray-400" />
                                                        Profile
                                                    </Link>
                                                )}
                                            </Menu.Item>

                                            <Menu.Item>
                                                {({ active }) => (
                                                    <button
                                                        className={classNames(
                                                            active
                                                                ? "bg-gray-50"
                                                                : "",
                                                            "flex w-full items-center px-4 py-2 text-sm text-red-600",
                                                        )}
                                                        onClick={wrapClick(() =>
                                                            setShowLogout(true),
                                                        )}>
                                                        <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-red-400" />
                                                        Logout
                                                    </button>
                                                )}
                                            </Menu.Item>
                                        </div>
                                    </Menu.Items>
                                </Transition>
                            </Menu>
                        </motion.div>
                    </motion.div>

                    <div className="flex-1 min-w-0 flex bg-background-500 flex-col overflow-hidden">
                        <Outlet />
                        <NotificationBanner />

                        {isKavaNotificationsOpen && (
                            <KavaNotificationsContainer
                                open={isKavaNotificationsOpen}
                                setOpen={setIsKavaNotificationsOpen}
                            />
                        )}
                    </div>
                </div>
            </div>

            <LogoutContainer
                open={showLogout}
                type="confirm"
                setOpen={setShowLogout}
            />
            <LogoutContainer
                open={showPrompt}
                type={showPrompt ? "prompt" : "confirm"}
                setOpen={setShowPrompt}
            />

            <AIChatPanel />
            <AIButton />
        </>
    );
};

export default AppLayout;
